import * as THREE from 'three'
import { MathUtils } from 'three'

import EventEmitter from "./utils/EventEmitter"
import Experience from "./Experience"
import FragmentShader from "./shaders/fragment.glsl"
import VertexShader from "./shaders/vertex.glsl"

export default class ParticleObjects extends EventEmitter
{
    constructor()
    {
        super()

        this.experience = new Experience()

        this.scene = this.experience.scene

        this.camera = this.experience.camera

        this.objectsLoader = this.experience.objectsLoader
        this.objectsLoader.on("ready", () => {this.spawnStaticParticles()})
        this.objectsLoader.on("ready", () => {this.spawnDynamicParticles()})
        
        this.resources = this.experience.resources
        
        this.doUpdate = false
        this.direction = false
        this.counter = 0
        this.state = -1
        this.timeOut = -1
        this.guiValues =
        {
            scroll: 0.0,
            mode: "Multiple",
            mouseRadiusMagnet: 0.0,
            mouseRadiusColor: 0.0,
            onHoverOffset: new THREE.Vector2(0.0, 6.0),
            opacityRange: new THREE.Vector2(1.0, 0.0),
            zRange: new THREE.Vector2(2.0, 3.6),
            colorHalo: 1.0,
            objectFocus: true,
            mouseFollowerDelta: 0.1,
            showMouseFollower: false,
            mouseMagnet: true,
            particlesSpeed: 5,
            objectsSpeed: 2,
            objectsFocusSpeed: 0.1,
            proxyMouseRadius: 20.0,
            proxyMouseOffset: -10.0
        }
        this.particleFixedScale = new THREE.Vector2(1.0, 4.0)
        this.dynamicParticleMaterial = new THREE.ShaderMaterial
        (
            {
                texture: this.objectsLoader.items.OnHoverOff,
                wireframe: true,
                transparent: true,
                fragmentShader: FragmentShader,
                vertexShader: VertexShader,
                uniforms:
                {
                    opacityRange: {value: this.guiValues.opacityRange},
                    zRange: {value: this.guiValues.zRange},
                    rotationMatrixOnHoverOff: {value: new THREE.Matrix4()},
                    rotationMatrixOnHoverOn: {value: new THREE.Matrix4()},
                    screenPointer: {value: new THREE.Vector2(0.0, 0.0)},
                    worldPointer: {value: new THREE.Vector3(0.0, 0.0, 0.0)},
                    mouseRadiusMagnet: {value: this.guiValues.mouseRadiusMagnet},
                    mouseRadiusColor: {value: this.guiValues.mouseRadiusColor},
                    onHoverOffset: {value: this.guiValues.onHoverOffset},
                    textureOnHoverOff: {value: this.objectsLoader.items.OnHoverOff},
                    textureOnHoverOn: {value: this.objectsLoader.items.OnHoverOn},
                    deltaOnHover: {value: 0.0},
                    colorHalo: {value: 1.0},
                    proxyLightPosition_0: {value: new THREE.Vector3(0.0, 0.0, 0.0)},
                    proxyLightPosition_1: {value: new THREE.Vector3(0.0, 0.0, 0.0)},
                    state: {value: 0}
                }
            }
        )
        this.staticParticleMaterials = 
        [
            new THREE.MeshBasicMaterial
            (
                {
                    color: new THREE.Color(0.145,0.302,0.275),
                    wireframe: false,
                    transparent: true,
                    opacity: 0.15
                }
            ),
            new THREE.MeshBasicMaterial
            (
                {
                    color: new THREE.Color(0.145,0.302,0.275),
                    wireframe: false,
                    transparent: true,
                    opacity: 0.08
                }
            ),
            new THREE.MeshBasicMaterial
            (
                {
                    color: new THREE.Color(0.145,0.302,0.275),
                    wireframe: false,
                    transparent: true,
                    opacity: 0.1
                }
            )
        ]

        this.wheelDirection = true
        this.wheelPartialValue = 0.0

        this.objectsStartPositions =
        [
            [ // DESKTOP 1920
                new THREE.Vector3(0.75, 0.0, 0.0),
                new THREE.Vector3(0.0, 1.5, 0.0),
                new THREE.Vector3(-0.9, -2.5, 0.0),
                new THREE.Vector3(-1.0, -3.5, 0.0),
                new THREE.Vector3(0.0, -6.5, 0.0),
                new THREE.Vector3(0.0, -7.0, 0.0),
                new THREE.Vector3(0.0, -8.0, 0.0)
            ],
            [ // IPAD AIR 820
                new THREE.Vector3(0.75, 0.0, 0.0),
                new THREE.Vector3(0.0, 1.5, 0.0),
                new THREE.Vector3(0.0, -2.5, 0.0),
                new THREE.Vector3(0.0, -3.5, 0.0),
                new THREE.Vector3(0.0, -6.5, 0.0),
                new THREE.Vector3(0.0, -7.0, 0.0),
                new THREE.Vector3(0.0, -8.0, 0.0)
            ]
            
        ]
        this.objectsStartRotation =
        [
            new THREE.Euler(0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0)),
            new THREE.Euler(0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0)),
            new THREE.Euler(0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0)),
            new THREE.Euler(0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0)),
            new THREE.Euler(0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0)),
            new THREE.Euler(0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0)),
            new THREE.Euler(0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0), 0.0 * (Math.PI / 180.0))
        ]
        this.objectsStartScale =
        [
            new THREE.Vector3(1.0, 1.0, 1.0),
            new THREE.Vector3(1.5, 1.5, 1.5),
            new THREE.Vector3(0.3, 0.3, 0.3),
            new THREE.Vector3(0.4, 0.4, 0.4),
            new THREE.Vector3(1.0, 1.0, 1.0),
            new THREE.Vector3(0.6, 0.6, 0.6),
            new THREE.Vector3(1.0, 1.0, 1.0),
        ]
        
        this.currentObjectRaycast = null
        this.delta = 3.0
        this.proxyMouseActive = true
        this.numberOfStaticParticles = 100
    }

    update()
    {
        if (!this.doUpdate) return
        if (!this.particles) return

        if (this.delta > 0) this.delta -= this.experience.time.deltaTime

        this.objectsBehaviour()
        this.dynamicParticlesIteration()
        this.staticParticlesIteration()
    }

    objectsBehaviour()
    {
        if (!this.doUpdate) return
        if (this.state < 0) return

        let speedTranslation = this.experience.time.deltaTime * this.guiValues.objectsSpeed
        let speedTranslationFocus = this.experience.time.deltaTime * this.guiValues.objectsFocusSpeed
     
        let positionX = 0.0
        let positionY = 0.0
        let positionZ = 0.0

        let delta = 0.0
        let lerpedX = 0.0
        let newLerpedPosition = new THREE.Vector3()

        if (this.experience.sizes.widthState == 0)
        {
            delta = MathUtils.inverseLerp(-0.8, this.experience.sizes.device["Desktop"], this.experience.sizes.width)

            if (this.state == 0 || this.state == 5)
            {
                lerpedX = MathUtils.lerp(this.objectsStartPositions[1][this.state].clone().x, this.objectsStartPositions[0][this.state].clone().x, delta)
            }
            else
            {
                // lerpedX = MathUtils.lerp(0.0, this.objectsStartPositions[0][this.state].clone().x, delta)
                lerpedX = this.experience.raycast.resize.position.x
            }

            newLerpedPosition = new THREE.Vector3(lerpedX, this.objectsStartPositions[0][this.state].y, this.objectsStartPositions[0][this.state].z)
        }

        let condition = (this.currentObjectRaycast == this.objectsLoader.items.Object_0.mesh && this.state == 0) || (this.currentObjectRaycast == this.objectsLoader.items.Object_1.mesh && this.state == 2) ||
                          (this.currentObjectRaycast == this.objectsLoader.items.Object_2.mesh && this.state == 3) || (this.currentObjectRaycast == this.objectsLoader.items.Object_3.mesh && this.state == 5)

        if (condition && this.guiValues.objectFocus && this.delta <= 0.0)
        {
            let newPosition = this.experience.raycast.currentPoint.clone()

            if (this.experience.sizes.widthState == 0)
            {
                newPosition.sub(newLerpedPosition.clone())
                newPosition.normalize().multiplyScalar(0.25)
                newPosition.add(newLerpedPosition.clone())
            }
            else
            {
                newPosition.sub(this.objectsStartPositions[this.experience.sizes.widthState][this.state].clone())
                newPosition.normalize().multiplyScalar(0.25)
                newPosition.add(this.objectsStartPositions[this.experience.sizes.widthState][this.state].clone())
            }

            positionX = MathUtils.lerp(this.particles.position.x, newPosition.x, speedTranslationFocus)
            positionY = MathUtils.lerp(this.particles.position.y, newPosition.y, speedTranslationFocus)
            positionZ = this.particles.position.z
        }
        else
        {
            if (this.experience.sizes.widthState == 0)
            {
                if (this.state == 6)
                {
                    positionX = MathUtils.lerp(this.particles.position.x, 0.0, speedTranslation)
                }
                else
                {
                    positionX = MathUtils.lerp(this.particles.position.x, newLerpedPosition.x, speedTranslation)
                }
                positionY = MathUtils.lerp(this.particles.position.y, newLerpedPosition.y, speedTranslation)
                positionZ = MathUtils.lerp(this.particles.position.z, newLerpedPosition.z, speedTranslation)
            }
            else
            {
                positionX = MathUtils.lerp(this.particles.position.x, this.objectsStartPositions[this.experience.sizes.widthState][this.state].x, speedTranslation)
                positionY = MathUtils.lerp(this.particles.position.y, this.objectsStartPositions[this.experience.sizes.widthState][this.state].y, speedTranslation)
                positionZ = MathUtils.lerp(this.particles.position.z, this.objectsStartPositions[this.experience.sizes.widthState][this.state].z, speedTranslation)
            }
        }

        this.particles.position.set(positionX, positionY, positionZ)

        this.objectsLoader.items.Object_0.mesh.position.set(positionX, positionY, positionZ)
        this.objectsLoader.items.Object_1.mesh.position.set(positionX, positionY, positionZ)
        this.objectsLoader.items.Object_2.mesh.position.set(positionX, positionY, positionZ)
        this.objectsLoader.items.Object_3.mesh.position.set(positionX, positionY, positionZ)
        this.objectsLoader.items.Molecule.position.set(positionX, positionY, positionZ)
        
        let speedRotation = 15.0 * Math.PI / 180.0

        if (this.state == 0)
        {
            this.particles.rotateOnAxis(new THREE.Vector3(1.0, 1.0, 0.0), 0.01 * (Math.PI / 180.0))

            this.objectsLoader.items.Object_0.mesh.rotation.set(this.particles.rotation.x, this.particles.rotation.y, this.particles.rotation.z)
        }
        else
        {
            let rotationX = MathUtils.lerp(this.particles.rotation.x, this.objectsStartRotation[this.state].x, speedRotation)
            let rotationY = MathUtils.lerp(this.particles.rotation.y, this.objectsStartRotation[this.state].y, speedRotation)
            let rotationZ = MathUtils.lerp(this.particles.rotation.z, this.objectsStartRotation[this.state].z, speedRotation)
                
            this.particles.rotation.set(rotationX, rotationY, rotationZ)
                
            this.objectsLoader.items.Object_1.mesh.rotation.set(rotationX, rotationY, rotationZ)
            this.objectsLoader.items.Object_2.mesh.rotation.set(rotationX, rotationY, rotationZ)
            this.objectsLoader.items.Object_3.mesh.rotation.set(rotationX, rotationY, rotationZ)
        }

        if (this.state == 5)
        {
            this.timeOut = setTimeout(() => {
                let lerp = MathUtils.lerp(this.objectsLoader.items.Molecule.scale.x, 1.0, 0.1)
                this.objectsLoader.items.Molecule.scale.set(lerp, lerp, lerp)
            }, 1000)
        }
        else
        {
            clearTimeout(this.timeOut);
            let lerp = MathUtils.lerp(this.objectsLoader.items.Molecule.scale.x, 0.0, 0.1)
            this.objectsLoader.items.Molecule.scale.set(lerp, lerp, lerp)
        }
    }
    
    dynamicParticlesIteration()
    {
        for (let i = 0; i < this.objectsLoader.numberOfElements; i++)
        {
            let currentParent = this.particles.children[i]
            let currentMesh = currentParent.children[0]

            this.dynamicParticlesTranslate(currentParent, i)
            this.dynamicParticlesRotation(currentParent, currentMesh)
            this.dynamicParticlesScale(currentParent, i)
            
            this.proxyMouse(currentParent, currentMesh)
            this.dynamicParticlesOnHover(currentParent, currentMesh)
            this.dynamicParticlesNotHover(currentParent, currentMesh)
        }
    }

    proxyMouse(currentParent, currentMesh)
    {
        if (this.camera.newScrollValue > 30.0 && this.camera.newScrollValue < 45.0) return
        if (!currentParent.onHoverProxy) return

        let mul = MathUtils.inverseLerp(this.guiValues.proxyMouseRadius * 0.1, 0.0, currentParent.distanceFromProxyMouse)
        let endLerp = MathUtils.clamp(this.particleFixedScale.y * mul, this.particleFixedScale.x * 2.0, this.particleFixedScale.y)
        let lerp = MathUtils.lerp(currentMesh.scale.x, endLerp, 0.1)
        
        currentMesh.scale.set(lerp, lerp, lerp)
        
        if (this.state < 0) return
        
        let offset = this.guiValues.proxyMouseOffset * 0.1
        
        let lerpPosition = new THREE.Vector3()
        
        let worldPoint = this.camera.proxyMouse.localToWorld(this.camera.proxyMouse.position.clone())
        let localPoint = currentParent.worldToLocal(worldPoint)
        lerpPosition.lerpVectors(currentMesh.position, localPoint.multiplyScalar(offset * 5.0 * mul), 0.1)
        
        currentMesh.position.set(lerpPosition.x, lerpPosition.y, lerpPosition.z)
    }

    dynamicParticlesOnHover(currentParent, currentMesh)
    {
        if (!currentParent.onHover) return

        let mul = MathUtils.inverseLerp(this.guiValues.mouseRadiusMagnet * 0.1, 0.0, currentParent.distanceFromMouse)

        let endLerp = MathUtils.clamp(this.particleFixedScale.y * mul, this.particleFixedScale.x * 2.0, this.particleFixedScale.y)
        let lerp = MathUtils.lerp(currentMesh.scale.x, endLerp, 0.1)

        currentMesh.scale.set(lerp, lerp, lerp)

        if (this.state < 0) return

        let offset = this.guiValues.onHoverOffset.y * 0.1

        let lerpPosition = new THREE.Vector3()
        if (this.guiValues.mouseMagnet)
        {
            let localPoint = currentParent.worldToLocal(this.experience.raycast.currentPoint.clone())
            lerpPosition.lerpVectors(currentMesh.position, localPoint.multiplyScalar(offset * 5.0 * mul), 0.1)
        }
        else
        {
            lerpPosition.lerpVectors(currentMesh.position, this.originalPosition[i].clone().multiplyScalar(offset * mul), 0.1)
        }

        currentMesh.position.set(lerpPosition.x, lerpPosition.y, lerpPosition.z)
    }

    dynamicParticlesNotHover(currentParent, currentMesh)
    {
        if (currentParent.onHover) return

        let lerp = MathUtils.lerp(currentMesh.scale.x, this.particleFixedScale.x, 0.05)
                
        currentMesh.scale.set(lerp, lerp, lerp)

        if (this.state < 0) return
        
        let lerpPositionX = MathUtils.lerp(currentMesh.position.x, 0.0, 0.05)
        let lerpPositionY = MathUtils.lerp(currentMesh.position.y, this.guiValues.onHoverOffset.x, 0.05)
        let lerpPositionZ = MathUtils.lerp(currentMesh.position.z, 0.0, 0.05)

        currentMesh.position.set(lerpPositionX, lerpPositionY, lerpPositionZ)
    }

    dynamicParticlesTranslate(currentParent, index)
    {
        let time = this.experience.time.deltaTime * this.guiValues.particlesSpeed

        if (index < this.originalPosition.length)
        {
            currentParent.position.lerp(this.originalPosition[index % this.originalPosition.length], time)
        }
        else
        {
            currentParent.position.lerp(new THREE.Vector3(0.0, 0.0, 0.0), time)
        }
        
    }

    dynamicParticlesRotation(currentParent, currentMesh)
    {
        currentMesh.rotateY(this.experience.time.deltaTime * 0.1)

        return

        if (this.guiValues.onHoverOffset.x > 0.0 || currentParent.onHover)
        {
            currentParent.rotateX(this.experience.time.deltaTime * currentParent.rotationValue.x * 0.1)
            currentParent.rotateY(this.experience.time.deltaTime * currentParent.rotationValue.y * 0.1)
            currentParent.rotateZ(this.experience.time.deltaTime * currentParent.rotationValue.z * 0.1)
        }
        else if (this.state == 0 || this.state == 2 || this.state == 3 || this.state == 5)
        {
            currentParent.quaternion.slerp(currentParent.originalRotations[this.state], 0.05)
        }
    }

    dynamicParticlesScale(currentParent, index)
    {
        if (this.state < 0) return

        let time = this.experience.time.deltaTime * this.guiValues.particlesSpeed

        if (index < this.originalPosition.length)
        {
            currentParent.scale.lerp(this.objectsStartScale[this.state], time)
        }
        else
        {
            currentParent.scale.lerp(new THREE.Vector3(0.000001, 0.000001, 0.000001), time * 10.0)
        }
    }

    staticParticlesIteration()
    {
        for (let i = 0; i < this.numberOfStaticParticles; i++)
        {
            let currentParent = this.staticParticles.children[i]
            let currentMesh = currentParent.children[0]

            this.staticParticlesTranslate(currentMesh)
            this.staticParticlesRotation(currentMesh)
        }
    }

    staticParticlesTranslate(currentMesh)
    {
        currentMesh.position.set(0.0, Math.sin(this.experience.time.elapsed * currentMesh.floatingValue  * 0.00005), 0.0)
    }

    staticParticlesRotation(currentMesh)
    {
        currentMesh.rotateY(this.experience.time.deltaTime * 0.25)
    }

    setOriginalRotations(parent, normals, index)
    {
        if (index < normals.length)
        {
            parent.lookAt(this.particles.localToWorld(normals[index].clone()))
            parent.originalRotations.push(parent.quaternion.clone())
        }
        else
        {
            parent.originalRotations.push(new THREE.Quaternion())
        }
    }

    spawnDynamicParticles()
    {
        this.particles = new THREE.Group()

        for (let i = 0; i < this.objectsLoader.numberOfElements; i++)
        {
            let parent = new THREE.Group()
            
            let objectMesh = this.resources.items.Particle_2.scene.children[0].clone()
            
            parent.add(objectMesh)

            parent.scale.set(0.0, 0.0, 0.0)
            
            objectMesh.material = this.dynamicParticleMaterial

            parent.name = "dynamic_particle_" + i
            parent.onHover = false
            parent.distanceFromMouse = 0.0
            parent.onHoverProxy = false
            parent.distanceFromProxyMouse = 0.0
            parent.rotationValue = ((Math.random() - 0.5) * 2.0, (Math.random() - 0.5) * 2.0, (Math.random() - 0.5) * 2.0)

            parent.originalRotations = []
            this.setOriginalRotations(parent, this.objectsLoader.items.Object_0.normals, i)
            this.setOriginalRotations(parent, this.objectsLoader.randomPoints, i)
            this.setOriginalRotations(parent, this.objectsLoader.items.Object_1.normals, i)
            this.setOriginalRotations(parent, this.objectsLoader.items.Object_2.normals, i)
            this.setOriginalRotations(parent, this.objectsLoader.randomPoints, i)
            this.setOriginalRotations(parent, this.objectsLoader.items.Object_3.normals, i)
            this.setOriginalRotations(parent, this.objectsLoader.randomPoints, i)
            parent.quaternion.set(0.0, 0.0, 0.0, 1.0)

            objectMesh.scale.set(this.particleFixedScale.x, this.particleFixedScale.x, this.particleFixedScale.x)

            this.particles.add(parent)

            objectMesh.position.set(0.0, 0.0, 0.0)
            objectMesh.rotation.set(0.0, 0.0, 0.0)

            parent.position.set(this.objectsLoader.randomPoints[i].x, this.objectsLoader.randomPoints[i].y, this.objectsLoader.randomPoints[i].z)
        }

        this.particles.position.set(this.objectsStartPositions[this.experience.sizes.widthState][0].x, this.objectsStartPositions[this.experience.sizes.widthState][0].y, this.objectsStartPositions[this.experience.sizes.widthState][0].z)

        this.scene.add(this.particles)

        this.newPoints = this.objectsLoader.items.Object_0.points

        this.originalPosition = this.objectsLoader.randomPoints
        this.originalRotation = this.objectsLoader.items.Object_0.normals
        this.resources.exitLoader();
        this.fadeOutReady()

        this.trigger('ready')
    }

    fadeOutReady()
    {
        this.doUpdate = true
        this.setState(0)
    }

    spawnStaticParticles()
    {
        this.staticParticles = new THREE.Group()

        for (let i = 0; i < this.numberOfStaticParticles; i++)
        {
            let parent = new THREE.Group()

            let objectParent = new THREE.Group()

            let objectMesh_0 = this.resources.items.WhireCube_01.scene.children[0].clone()

            let objectMaterial = new THREE.MeshBasicMaterial
            (
                {
                    map: this.resources.items.WhireCube_Texture,
                    transparent: true,
                    opacity: 0.5
                }
            )

            objectMesh_0.material = objectMaterial
            objectParent.add(objectMesh_0)
            // objectMesh_0.position.set(0.0075, 0.0, 0.0)
            
            // let objectMesh_1 = this.resources.items.WhireCube_00.scene.children[0].clone()
            // objectMesh_1.material = this.staticParticleMaterials[1]
            // objectParent.add(objectMesh_1)
            // objectMesh_1.position.set(0.0, 0.0075, 0.0)
            
            // let objectMesh_2 = this.resources.items.WhireCube_00.scene.children[0].clone()
            // objectMesh_2.material = this.staticParticleMaterials[2]
            // objectParent.add(objectMesh_2)
            // objectMesh_2.position.set(0.0, 0.0, 0.0075)

            parent.add(objectParent)

            parent.name = "static_particle_" + i
            objectParent.floatingValue = Math.random() * 0.5 + 0.5

            this.staticParticles.add(parent)

            objectParent.position.set(0.0, 0.1, 0.0)

            let randomPosition = this.getRandomPosition()

            parent.position.set(randomPosition.x, randomPosition.y, randomPosition.z)

            let newScale = MathUtils.inverseLerp(-6.0, -4.0, parent.position.z) * 3.5

            objectMesh_0.scale.set(newScale, newScale, newScale)
        }

        this.scene.add(this.staticParticles)
    }

    getRandomPosition()
    {
        let randomX = (Math.random() * 5.0) * (Math.random() < 0.5 ? 1.0 : - 1.0)
        let randomY = (Math.random() * 10.0) * (Math.random() < 0.5 ? 1.0 : - 1.0)
        let randomZ = (Math.random() * -2.0) - 4.0

        let randomPosition = new THREE.Vector3(randomX, randomY, randomZ)

        return randomPosition
    }

    setState(forceState = -1)
    {
        this.guiValues.scroll = this.camera.newScrollValue

        let newState = -1

        if (forceState < 0)
        {
            if (this.guiValues.scroll >= 0.0 && this.guiValues.scroll < 12.5) newState = 0
            else if (this.guiValues.scroll >= 12.5 && this.guiValues.scroll < 25.0) newState = 1
            else if (this.guiValues.scroll >= 25.0 && this.guiValues.scroll < 37.5) newState = 2
            else if (this.guiValues.scroll >= 37.5 && this.guiValues.scroll < 50.0) newState = 3
            else if (this.guiValues.scroll >= 50.0 && this.guiValues.scroll < 75.0) newState = 4
            else if (this.guiValues.scroll >= 75.0 && this.guiValues.scroll < 87.5) newState = 5
            else if (this.guiValues.scroll >= 87.5 && this.guiValues.scroll <= 100.0) newState = 6
        }
        else
        {
            newState = forceState
        }

        if (newState >= 0 && this.state != newState)
        {
            this.state = newState
            this.wheelDirection = this.experience.camera.wheelDirection
            this.dynamicParticleMaterial.uniforms.state.value = this.state
            this.objectsLoader.items.Molecule.material.opacity = 0.0
            this.delta = 3.0
        }
        
        if (this.state == 0)
        {
            this.originalPosition = this.objectsLoader.items.Object_0.points
            this.originalRotation = this.objectsLoader.items.Object_0.normals
            this.objectsLoader.items.Molecule.material.opacity = 0.05
            this.setMouseRadiusMagnet(0.4)
            this.setMouseRadiusColor(0.6)
        }
        else if (this.state == 2)
        {
            this.originalPosition = this.objectsLoader.items.Object_1.points
            this.originalRotation = this.objectsLoader.items.Object_1.normals
            this.objectsLoader.items.Molecule.material.opacity = 0.05
            this.setMouseRadiusMagnet(0.025)
            this.setMouseRadiusColor(0.1)
        }
        else if (this.state == 3)
        {
            this.originalPosition = this.objectsLoader.items.Object_2.points
            this.originalRotation = this.objectsLoader.items.Object_2.normals
            this.objectsLoader.items.Molecule.material.opacity = 0.05
            this.setMouseRadiusMagnet(0.025)
            this.setMouseRadiusColor(0.1)
        }
        else if (this.state == 5)
        {
            this.originalPosition = this.objectsLoader.items.Object_3.points
            this.originalRotation = this.objectsLoader.items.Object_3.normals
            this.objectsLoader.items.Molecule.material.opacity = 0.05
            this.setMouseRadiusMagnet(0.05)
            this.setMouseRadiusColor(0.1)
        }
        else
        {
            this.originalPosition = this.objectsLoader.randomPoints
            this.originalRotation = this.objectsLoader.randomPoints
            this.objectsLoader.items.Molecule.material.opacity = 0.05
            this.setMouseRadiusMagnet(0.0)
            this.setMouseRadiusColor(0.0)
        }
    }

    setMode(value)
    {
        this.guiValues.mode = value
    }

    setMouseRadiusMagnet(value)
    {
        this.guiValues.mouseRadiusMagnet = value
        this.dynamicParticleMaterial.uniforms.mouseRadiusMagnet.value = value
    }

    setMouseRadiusColor(value)
    {
        this.guiValues.mouseRadiusColor = value
        this.dynamicParticleMaterial.uniforms.mouseRadiusColor.value = value
    }

    setOnHoverOffset(value)
    {
        this.guiValues.onHoverOffset = new THREE.Vector2(value.x, value.y)
        this.dynamicParticleMaterial.uniforms.onHoverOffset.value = value.y
    }

    setOpacityRange(a, b)
    {
        this.guiValues.opacityRange = new THREE.Vector2(a, b)
        this.dynamicParticleMaterial.uniforms.opacityRange.value = new THREE.Vector2(a, b)
    }

    setZRange(a, b)
    {
        this.guiValues.zRange = new THREE.Vector2(a, b)
        this.dynamicParticleMaterial.uniforms.zRange.value = new THREE.Vector2(a, b)
    }

    setColorHalo(value)
    {
        this.guiValues.colorHalo = value
        this.dynamicParticleMaterial.uniforms.colorHalo.value = value
    }

    setShowMouseFollower(value)
    {
        this.guiValues.showMouseFollower = value
        this.experience.raycast.setShowMouseFollower(value)
    }
}